import { getExtension } from "@medplum/core";
import type { Extension, QuestionnaireItem } from "@medplum/fhirtypes";
import { v4 as uuidv4 } from "uuid";
import { createLogger } from "@/lib/logger";
import { normalizeAnswerOptionForPersistence } from "@/lib/utils/questionnaires/legacy-answer-option-repair";

const logger = createLogger("Question");

// Update QuestionType to include new types
export type QuestionType =
  | "text"
  | "paragraph"
  | "instruction"
  | "single-select"
  | "multi-select"
  | "score"
  | "boolean"
  | "date"
  | "integer"
  | "decimal"
  | "group"
  | "signature";

export type ValidationRule =
  | { type: "min" | "max"; value: number; message?: string }
  | { type: "minLength" | "maxLength"; value: number; message?: string }
  | { type: "pattern"; value: string; message?: string }
  | { type: "custom"; value: (input: unknown) => boolean; message?: string };

/**
 * Question class wraps FHIR QuestionnaireItem to provide a clean API
 * for working with questionnaire questions without exposing FHIR complexity.
 */
export class Question {
  private resource: QuestionnaireItem;

  // Extension URL prefixes
  private static readonly VALIDATION_URL_PREFIX = "vibrant.question.validation." as const;
  private static readonly METADATA_URL_PREFIX = "vibrant.question.metadata." as const;
  private static readonly SIGNATURE_METADATA_KEY = "signatureType" as const;
  private static readonly INSTRUCTION_METADATA_KEY = "instructionType" as const;
  private static readonly INSTRUCTION_TEXT_METADATA_KEY = "instructionText" as const;
  // `step` is never written by this class's own score defaults anymore, but must stay in the
  // clear-list: pre-existing resources can carry it from before that removal (and from the
  // retired legacy builder, which used to write it). Without it here, clearScoreMetadata()
  // leaves an orphaned `vibrant.question.metadata.step` extension behind whenever a score
  // question is retyped to something else.
  private static readonly SCORE_METADATA_KEYS = [
    "scoreType",
    "minValue",
    "minLabel",
    "maxValue",
    "maxLabel",
    "step",
  ] as const;
  // Named for what it does here (starter-option seeding), not the publish-time minimum in
  // lib/constants/questionnaires.ts's MIN_CHOICE_OPTION_COUNT (currently 1) — the two are
  // independent and don't need to match; seeding more than the minimum is harmless.
  private static readonly DEFAULT_SEEDED_OPTION_COUNT = 2 as const;

  // Constants
  private static readonly MULTIPLE_RESPONSE_ALLOWED_TYPES: QuestionType[] = [
    "text",
    "paragraph",
    "group",
  ] as const;

  // Error messages
  private static readonly ERROR_MESSAGES = {
    MULTIPLE_RESPONSES_TYPE_RESTRICTION: "This question type does not support multiple responses",
    NESTED_GROUPS_NOT_ALLOWED: "Group questions cannot contain other group questions",
    CANNOT_CHANGE_TO_GROUP: "Cannot change subquestion to group type",
  } as const;

  constructor(item?: QuestionnaireItem) {
    this.resource = item || {
      linkId: uuidv4(),
      type: "string",
      text: "",
    };
  }

  //------------------------------------------------------------------------------------------------
  //                                     VIRTUAL PROPERTIES
  //------------------------------------------------------------------------------------------------

  /**
   * The unique identifier for this question
   */
  get id(): string {
    return this.resource.linkId;
  }

  /**
   * The question text displayed to the user
   */
  get text(): string {
    return this.resource.text || "";
  }

  set text(value: string) {
    this.resource.text = value;
  }

  /**
   * Optional instructional text displayed alongside the question.
   */
  get instructionText(): string {
    return this.getMetadata()[Question.INSTRUCTION_TEXT_METADATA_KEY] || "";
  }

  set instructionText(value: string) {
    if (!value) {
      this.setMetadata(Question.INSTRUCTION_TEXT_METADATA_KEY, undefined);
      return;
    }
    this.setMetadata(Question.INSTRUCTION_TEXT_METADATA_KEY, value);
  }

  /**
   * The type of question (maps FHIR types to our simplified types)
   */
  get type(): QuestionType {
    // Handle undefined type - default to "text"
    if (!this.resource.type) {
      return "text";
    }

    if (this.isSignatureQuestion()) {
      return "signature";
    }

    if (this.isInstructionQuestion()) {
      return "instruction";
    }

    // Map choice to single-select and open-choice to multi-select
    if (this.resource.type === "choice") {
      return "single-select";
    }

    if (this.resource.type === "open-choice") {
      return "multi-select";
    }

    // Check for score type via metadata
    if (this.resource.type === "integer" && this.getMetadata().scoreType === "true") {
      return "score";
    }

    const typeMap: Record<string, QuestionType> = {
      string: "text",
      text: "paragraph", // FHIR "text" = multi-line; "string" = single-line
      boolean: "boolean",
      date: "date",
      decimal: "decimal",
      integer: "integer",
      group: "group",
      display: "text",
    };
    return typeMap[this.resource.type] || "text";
  }

  set type(value: QuestionType) {
    const fhirTypeMap: Record<QuestionType, QuestionnaireItem["type"]> = {
      text: "string",
      paragraph: "text", // FHIR "text" = multi-line; "string" = single-line
      instruction: "text",
      "single-select": "choice",
      "multi-select": "open-choice",
      score: "integer",
      boolean: "boolean",
      date: "date",
      integer: "integer",
      decimal: "decimal",
      group: "group",
      signature: "string",
    };
    this.resource.type = fhirTypeMap[value];
    this.normalizeForType({ value });

    if (value === "instruction") {
      this.resource.required = false;
      this.resource.repeats = false;
      this.setMetadata(Question.INSTRUCTION_METADATA_KEY, "true");
    } else {
      this.setMetadata(Question.INSTRUCTION_METADATA_KEY, undefined);
    }

    // Set score metadata
    if (value === "score") {
      this.setMetadata("scoreType", "true");
      // Set default score settings if not already set
      if (!this.getMetadata().minValue) {
        this.setMetadata("minValue", "1");
        this.setMetadata("minLabel", "Strongly Disagree");
        this.setMetadata("maxValue", "5");
        this.setMetadata("maxLabel", "Strongly Agree");
      }
    } else {
      this.clearScoreMetadata();
    }

    if (value === "signature") {
      this.setMetadata(Question.SIGNATURE_METADATA_KEY, "true");
    } else {
      this.setMetadata(Question.SIGNATURE_METADATA_KEY, undefined);
    }
  }

  /**
   * Whether this question is required
   */
  get required(): boolean {
    return this.resource.required || false;
  }

  set required(value: boolean) {
    this.resource.required = value;
  }

  /**
   * Whether this question is read-only
   */
  get readOnly(): boolean {
    return this.resource.readOnly || false;
  }

  set readOnly(value: boolean) {
    this.resource.readOnly = value;
  }

  /**
   * Whether this question can have multiple answers
   */
  get repeats(): boolean {
    return this.resource.repeats || false;
  }

  set repeats(value: boolean) {
    this.resource.repeats = value;
  }

  /**
   * Whether this question allows multiple responses. Gated only by question type
   * (see `canHaveMultipleResponses`) - top-level questions and page subquestions are both
   * eligible, since every ordinary question lives inside a Page under the page-first model.
   */
  get allowMultipleResponses(): boolean {
    // Only certain question types can have multiple responses
    if (!this.canHaveMultipleResponses()) {
      return false;
    }

    // Now we just use the repeats property directly
    return this.resource.repeats || false;
  }

  set allowMultipleResponses(value: boolean) {
    // Only certain question types can have multiple responses
    if (!this.canHaveMultipleResponses()) {
      throw new Error(Question.ERROR_MESSAGES.MULTIPLE_RESPONSES_TYPE_RESTRICTION);
    }

    // Now we just set repeats directly
    this.resource.repeats = value;
  }

  /**
   * Check if this is a sub-question of a group
   */
  isSubQuestion(): boolean {
    // Sub-questions have linkIds with dots (e.g., "group1.subquestion1")
    return this.resource.linkId?.includes(".") || false;
  }

  /**
   * Check if this is a top-level question (not a sub-question of a group)
   */
  isTopLevel(): boolean {
    return !this.isSubQuestion();
  }

  /**
   * Get the parent linkId prefix from a sub-question's linkId
   */
  getParentPrefix(): string | undefined {
    if (!this.isSubQuestion() || !this.resource.linkId) {
      return undefined;
    }
    return this.resource.linkId.substring(0, this.resource.linkId.lastIndexOf("."));
  }

  /**
   * Check if this question type supports multiple responses
   */
  canHaveMultipleResponses(): boolean {
    return Question.MULTIPLE_RESPONSE_ALLOWED_TYPES.includes(this.type);
  }

  /**
   * Get answer options for choice questions
   */
  get options(): { value: string; label: string; id?: string }[] {
    if (!this.resource.answerOption) return [];

    return this.resource.answerOption.flatMap((option) => {
      // A present (even empty) `valueString` is what the live builder writes while a user is
      // actively clearing an option's text (see QuestionOptionsEditor) - never repair/drop it,
      // or the row disappears out from under them mid-edit. Only an option with no `value[x]`
      // key at all is eligible for the legacy id-only-placeholder humanize-or-drop repair below;
      // `sanitizeQuestionnaireItemForPersistence` (converters.ts) still strips truly-blank
      // options at actual save time.
      const normalizedOption =
        option.valueString !== undefined
          ? option
          : normalizeAnswerOptionForPersistence({ answerOption: option });
      if (!normalizedOption) {
        return [];
      }

      if (normalizedOption.valueCoding) {
        return {
          value: normalizedOption.valueCoding.code || "",
          label: normalizedOption.valueCoding.display || normalizedOption.valueCoding.code || "",
          id: normalizedOption.id,
        };
      }

      return {
        value: normalizedOption.valueString || "",
        label: normalizedOption.valueString || "",
        id: normalizedOption.id,
      };
    });
  }

  /**
   * Set answer options for choice questions
   */
  setOptions(options: { value: string; label: string; id?: string }[]) {
    this.resource.answerOption = options.map((option) => {
      const hasDistinctCodeAndLabel =
        option.value !== "" && option.label !== "" && option.value !== option.label;

      // Preserve coded options when the stored answer value differs from the label shown to people.
      if (hasDistinctCodeAndLabel) {
        return {
          id: option.id,
          valueCoding: {
            code: option.value,
            display: option.label,
          },
        };
      }

      return {
        id: option.id,
        valueString: option.value || option.label,
      };
    });
  }

  /**
   * Get child questions (for group questions)
   */
  get children(): Question[] {
    if (!this.resource.item) return [];
    return this.resource.item.map((item) => new Question(item));
  }

  /**
   * Check if this is a group question
   */
  get isGroup(): boolean {
    return this.type === "group";
  }

  /**
   * Get component type for UI rendering
   */
  get componentType(): string | undefined {
    return this.getExtensionValue("Component");
  }

  set componentType(value: string | undefined) {
    this.setExtensionValue("Component", value);
  }

  /**
   * Get label (distinct from question text)
   */
  get label(): string | undefined {
    return this.getExtensionValue("Label");
  }

  set label(value: string | undefined) {
    this.setExtensionValue("Label", value);
  }

  /**
   * Get prefix text
   */
  get prefix(): string | undefined {
    return this.getExtensionValue("Prefix");
  }

  set prefix(value: string | undefined) {
    this.setExtensionValue("Prefix", value);
  }

  /**
   * Get suffix text
   */
  get suffix(): string | undefined {
    return this.getExtensionValue("Suffix");
  }

  set suffix(value: string | undefined) {
    this.setExtensionValue("Suffix", value);
  }

  /**
   * Get SNOMED code
   */
  get snomedCode(): string | undefined {
    return this.getExtensionValue("SNOMED");
  }

  set snomedCode(value: string | undefined) {
    this.setExtensionValue("SNOMED", value);
  }

  /**
   * Get ICD-10 code
   */
  get icd10Code(): string | undefined {
    return this.getExtensionValue("ICD10");
  }

  set icd10Code(value: string | undefined) {
    this.setExtensionValue("ICD10", value);
  }

  //------------------------------------------------------------------------------------------------
  //                                       INSTANCE METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Add or update a child question (for group questions).
   *
   * Upserts by linkId: if a child with the same linkId already exists it is
   * replaced in place, never appended. A blind append here cloned sub-questions
   * forward on every edit, producing the "same text, sibling copies" duplication
   * (ENG-4613). New children (unique linkId) are still appended.
   */
  addChild(question: Question): Question {
    if (!this.resource.item) {
      this.resource.item = [];
    }
    const serialized = question.serialize();
    const existingIndex = this.resource.item.findIndex((item) => item.linkId === serialized.linkId);
    if (existingIndex !== -1) {
      this.resource.item[existingIndex] = serialized;
      return this;
    }
    this.resource.item.push(serialized);
    return this;
  }

  /**
   * Remove a child question by id
   */
  removeChild(questionId: string): Question {
    if (this.resource.item) {
      this.resource.item = this.resource.item.filter((item) => item.linkId !== questionId);
    }
    return this;
  }

  /**
   * Get a child question by id
   */
  getChild(questionId: string): Question | undefined {
    if (!this.resource.item) return undefined;
    const childItem = this.resource.item.find((item) => item.linkId === questionId);
    return childItem ? new Question(childItem) : undefined;
  }

  /**
   * Update a child question
   */
  updateChild(questionId: string, updater: (question: Question) => void): Question {
    if (!this.resource.item) return this;

    const index = this.resource.item.findIndex((item) => item.linkId === questionId);
    if (index !== -1) {
      const childQuestion = new Question(this.resource.item[index]);
      updater(childQuestion);
      this.resource.item[index] = childQuestion.serialize();
    }

    return this;
  }

  /**
   * Add a subquestion (alias for addChild with validation)
   * Validates that we're not adding a group to a group
   * Automatically sets proper linkId for sub-questions
   */
  addSubQuestion(question: Question): Question {
    // Prevent nested groups
    if (question.type === "group") {
      throw new Error(Question.ERROR_MESSAGES.NESTED_GROUPS_NOT_ALLOWED);
    }

    // Re-scope the linkId under THIS group unless it's already there. Covers both a brand-new
    // question (no dot yet) and one moved here from a different parent (outline drag-and-drop) -
    // the latter still has a dot, but a foreign one, so a naive "already has a dot" check would
    // leave it pointing at its old parent's linkId.
    const ownPrefix = `${this.resource.linkId}.`;
    if (!question.resource.linkId.startsWith(ownPrefix)) {
      const localId = question.isSubQuestion()
        ? question.resource.linkId.substring(question.resource.linkId.lastIndexOf(".") + 1)
        : question.resource.linkId;
      question.resource.linkId = `${this.resource.linkId}.${localId}`;
    }

    return this.addChild(question);
  }

  /**
   * Remove a subquestion by id (alias for removeChild)
   */
  removeSubQuestion(questionId: string): Question {
    return this.removeChild(questionId);
  }

  /**
   * Update a subquestion
   */
  updateSubQuestion(
    questionId: string,
    updates: Partial<{
      text: string;
      required: boolean;
      readOnly: boolean;
      type: QuestionType;
      allowMultipleResponses: boolean;
      options?: { value: string; label: string; id?: string }[];
      metadata?: Record<string, string | number | boolean | null | undefined>;
    }>,
  ): Question {
    return this.updateChild(questionId, (question) => {
      if (updates.text !== undefined) question.text = updates.text;
      if (updates.required !== undefined) question.required = updates.required;
      if (updates.readOnly !== undefined) question.readOnly = updates.readOnly;
      if (updates.type !== undefined) {
        // Prevent changing to group type
        if (updates.type === "group") {
          throw new Error(Question.ERROR_MESSAGES.CANNOT_CHANGE_TO_GROUP);
        }
        question.type = updates.type;
      }
      if (updates.allowMultipleResponses !== undefined) {
        question.allowMultipleResponses = updates.allowMultipleResponses;
      }
      if (updates.options !== undefined) question.setOptions(updates.options);
      if (updates.metadata !== undefined) {
        Object.entries(updates.metadata).forEach(([key, value]) => {
          question.setMetadata(key, value);
        });
      }
    });
  }

  /**
   * Get all subquestions (alias for children)
   */
  getSubQuestions(): Question[] {
    return this.children;
  }

  /**
   * Reorder subquestions within a group
   */
  reorderSubQuestions(fromIndex: number, toIndex: number): Question {
    if (!this.resource.item || !this.isGroup) return this;

    const items = [...this.resource.item];

    // Validate indices are within bounds
    if (fromIndex < 0 || fromIndex >= items.length || toIndex < 0 || toIndex >= items.length) {
      // Return current instance without modification for invalid indices
      return this;
    }

    const [movedItem] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, movedItem);
    this.resource.item = items;

    return this;
  }

  /**
   * Validate that this group question only contains simple questions
   */
  validateGroupStructure(): boolean {
    if (!this.isGroup) return true;

    return this.children.every((child) => child.type !== "group");
  }

  /**
   * Get validation rules
   */
  getValidationRules(): ValidationRule[] {
    const rules: ValidationRule[] = [];

    if (!this.resource.extension) return rules;

    this.resource.extension.forEach((ext) => {
      if (ext.url?.startsWith(Question.VALIDATION_URL_PREFIX) && ext.valueString) {
        try {
          const validationType = ext.url.substring(Question.VALIDATION_URL_PREFIX.length);
          const ruleData = JSON.parse(ext.valueString);

          // Construct the properly typed ValidationRule based on the validation type
          switch (validationType) {
            case "min":
            case "max":
            case "minLength":
            case "maxLength":
              rules.push({
                type: validationType as "min" | "max" | "minLength" | "maxLength",
                value: Number(ruleData.value),
                message: ruleData.message,
              });
              break;
            case "pattern":
              rules.push({
                type: "pattern",
                value: String(ruleData.value),
                message: ruleData.message,
              });
              break;
            case "custom":
              // Note: Custom validation functions cannot be serialized/deserialized from JSON
              // This would need special handling if custom validations are stored
              logger.warn("Custom validation functions cannot be deserialized from JSON");
              break;
            default:
              logger.warn(`Unknown validation type: ${validationType}`);
          }
        } catch (e) {
          logger.warn(`Failed to parse validation rule: ${ext.url}`, e);
        }
      }
    });

    return rules;
  }

  /**
   * Set validation rules
   */
  setValidationRules(rules: ValidationRule[]): Question {
    // Remove existing validation extensions
    const extensions = this.ensureExtensions();
    const filteredExtensions = extensions.filter(
      (ext) => !ext.url?.startsWith(Question.VALIDATION_URL_PREFIX),
    );
    this.resource.extension = filteredExtensions;

    // Add new validation rules
    rules.forEach((rule) => {
      filteredExtensions.push({
        url: `${Question.VALIDATION_URL_PREFIX}${rule.type}`,
        valueString: JSON.stringify({
          value: rule.value,
          message: rule.message,
        }),
      });
    });

    return this;
  }

  /**
   * Get all metadata as a record
   */
  getMetadata(): Record<string, string> {
    const metadata: Record<string, string> = {};

    if (!this.resource.extension) return metadata;

    this.resource.extension.forEach((ext) => {
      if (ext.url?.startsWith(Question.METADATA_URL_PREFIX)) {
        const key = ext.url.substring(Question.METADATA_URL_PREFIX.length);
        if (ext.valueString !== undefined) {
          metadata[key] = ext.valueString;
        }
      }
    });

    return metadata;
  }

  /**
   * Set metadata value
   */
  setMetadata(key: string, value: string | number | boolean | null | undefined): Question {
    const url = `${Question.METADATA_URL_PREFIX}${key}`;

    const extensions = this.ensureExtensions();

    // Remove existing extension with same key
    this.resource.extension = extensions.filter((ext) => ext.url !== url);

    if (value !== undefined && value !== null) {
      this.resource.extension.push({
        url,
        valueString: String(value),
      });
    }

    return this;
  }

  /**
   * Clones this question, reminting descendant IDs when the root identity changes.
   */
  clone(newId?: string): Question {
    const clonedItem: QuestionnaireItem = JSON.parse(JSON.stringify(this.resource));
    const parentPrefix = this.getParentPrefix();
    const generatedId = parentPrefix ? `${parentPrefix}.${uuidv4()}` : uuidv4();
    const clonedId = newId ?? generatedId;
    clonedItem.linkId = clonedId;

    if (clonedId !== this.id && clonedItem.item) {
      Question.remintDescendantLinkIds(clonedItem.item, clonedId);
    }

    return new Question(clonedItem);
  }

  /**
   * Serialize to FHIR QuestionnaireItem
   */
  serialize(): QuestionnaireItem {
    return this.resource;
  }

  /**
   * Create a Question from a QuestionnaireItem
   */
  static fromFHIR(item: QuestionnaireItem): Question {
    return new Question(item);
  }

  /**
   * Create a new Question with basic properties
   */
  static create(props: {
    text: string;
    type?: QuestionType;
    required?: boolean;
    id?: string;
  }): Question {
    const question = new Question();
    if (props.id) question.resource.linkId = props.id;
    question.text = props.text;
    if (props.type) question.type = props.type;
    if (props.required !== undefined) question.required = props.required;
    return question;
  }

  //------------------------------------------------------------------------------------------------
  //                                       PRIVATE METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Gives every descendant a unique ID rooted under its cloned parent.
   */
  private static remintDescendantLinkIds(items: QuestionnaireItem[], parentId: string): void {
    items.forEach((item) => {
      item.linkId = `${parentId}.${uuidv4()}`;
      if (item.item) {
        Question.remintDescendantLinkIds(item.item, item.linkId);
      }
    });
  }

  /**
   * Ensure extensions array is initialized and return it
   */
  private ensureExtensions(): Extension[] {
    if (!this.resource.extension) {
      this.resource.extension = [];
    }
    return this.resource.extension;
  }

  private getExtensionValue(url: string): string | undefined {
    const ext = getExtension(this.resource, url);
    return ext?.valueString;
  }

  private setExtensionValue(url: string, value: string | undefined): void {
    const extensions = this.ensureExtensions();

    // Remove existing extension
    this.resource.extension = extensions.filter((ext) => ext.url !== url);

    // Add new extension if value is provided
    if (value !== undefined) {
      this.resource.extension.push({
        url,
        valueString: value,
      });
    }
  }

  /**
   * Returns true when the question is configured as a typed signature.
   */
  private isSignatureQuestion(): boolean {
    if (this.resource.type !== "string" && this.resource.type !== "text") {
      return false;
    }

    return this.getMetadata()[Question.SIGNATURE_METADATA_KEY] === "true";
  }

  private isInstructionQuestion(): boolean {
    return this.getMetadata()[Question.INSTRUCTION_METADATA_KEY] === "true";
  }

  /**
   * Keeps the FHIR payload aligned with the selected builder question type.
   */
  private normalizeForType({ value }: { value: QuestionType }): void {
    if (value === "group") {
      this.ensureGroupHasSubQuestion();
    } else {
      delete this.resource.item;
    }

    if (value === "single-select" || value === "multi-select") {
      this.ensureMinimumChoiceOptions();
    } else {
      delete this.resource.answerOption;
    }

    if (!Question.MULTIPLE_RESPONSE_ALLOWED_TYPES.includes(value)) {
      delete this.resource.repeats;
    }
  }

  /**
   * Seeds valid starter options so select questions can autosave immediately.
   */
  private ensureMinimumChoiceOptions(): void {
    const options = [...this.options];

    for (
      let optionNumber = 1;
      options.length < Question.DEFAULT_SEEDED_OPTION_COUNT;
      optionNumber += 1
    ) {
      const label = `Option ${optionNumber}`;
      const doesOptionExist = options.some(
        (option) => option.label === label || option.value === label,
      );
      if (doesOptionExist) {
        continue;
      }

      options.push({
        id: uuidv4(),
        value: label,
        label,
      });
    }

    this.setOptions(options);
  }

  /**
   * Seeds one child question so new groups stay structurally valid.
   */
  private ensureGroupHasSubQuestion(): void {
    if ((this.resource.item?.length ?? 0) > 0) {
      return;
    }

    // Empty text (not a "New text question" default) so the builder shows the type's
    // placeholder until the user actually edits the title.
    const defaultSubQuestion = Question.create({
      text: "",
      type: "text",
      required: false,
    });
    this.addSubQuestion(defaultSubQuestion);
  }

  /**
   * Clears all score-only metadata when the type changes away from score.
   */
  private clearScoreMetadata(): void {
    Question.SCORE_METADATA_KEYS.forEach((metadataKey) => {
      this.setMetadata(metadataKey, undefined);
    });
  }
}
